import { rectAlignToPixels, type Mat3 } from '../core/math';
import type { DocumentMeta, Point, Rect } from '../core/types';
import { Texture } from '../gl/texture';
import type { Engine } from './engine';
import { pixelOp, selectionOp, type EngineHistoryOp } from './historyOps';
import { clearSelection, copyPixels, placementMatrix } from './ops';

/** Scratch slot for the preview of a layer with its floating pixels on top. */
const FLOAT_PREVIEW = 6;

function offsetRect(rect: Rect, offset: Point): Rect {
  return { ...rect, x: rect.x + offset.x, y: rect.y + offset.y };
}

/**
 * The selected pixels lifted off a layer, as Photoshop's floating selection.
 * Dragging with the Move tool while an area is selected cuts those pixels out
 * and carries them; the hole stays behind until the drag is undone.
 */
export class FloatingSelection {
  private constructor(
    private readonly engine: Engine,
    readonly rect: Rect,
    private readonly texture: Texture,
    private readonly liftOps: EngineHistoryOp[],
  ) {}

  /**
   * Cuts the selected pixels out of the layer. With `copy` the layer keeps them,
   * which is the Alt-drag variant.
   */
  static lift(
    engine: Engine,
    doc: DocumentMeta,
    layerId: string,
    copy: boolean,
  ): FloatingSelection | null {
    const lifted = copyPixels(engine, doc, layerId);
    if (!lifted || lifted.rect.width <= 0 || lifted.rect.height <= 0) return null;

    const texture = Texture.create(
      engine.gl,
      lifted.rect.width,
      lifted.rect.height,
      'rgba8',
      'linear',
    );
    texture.uploadPixels(
      { x: 0, y: 0, width: lifted.rect.width, height: lifted.rect.height },
      lifted.data,
    );

    const ops = copy ? [] : (clearSelection(engine, doc, layerId) ?? []);
    return new FloatingSelection(engine, lifted.rect, texture, ops);
  }

  /** Layer pixels with the float drawn on top, for the live preview. */
  preview(doc: DocumentMeta, layerId: string, offset: Point): Texture {
    const resources = this.engine.resourcesFor(doc);
    const surface = resources.ensureLayer(layerId).surface;
    const target = resources.rgba(FLOAT_PREVIEW);
    this.engine.renderer.blendLayer(target.framebuffer, surface.texture, {
      texture: this.texture,
      srcMatrix: this.matrixFor(doc, offset),
      mask: null,
      opacity: 1,
      blendMode: 'normal',
    });
    return target.texture;
  }

  /**
   * Bakes the float into the layer and moves the outline with it, so the pixels
   * stay selected where they landed.
   */
  drop(doc: DocumentMeta, layerId: string, offset: Point): EngineHistoryOp[] {
    const resources = this.engine.resourcesFor(doc);
    const surface = resources.ensureLayer(layerId).surface;
    const region = rectAlignToPixels(
      {
        x: Math.min(this.rect.x, this.rect.x + offset.x),
        y: Math.min(this.rect.y, this.rect.y + offset.y),
        width: this.rect.width + Math.abs(offset.x),
        height: this.rect.height + Math.abs(offset.y),
      },
      doc.width,
      doc.height,
    );

    const before = surface.readPixels(region);
    const scratch = resources.rgba(FLOAT_PREVIEW);
    this.engine.renderer.blendLayer(scratch.framebuffer, surface.texture, {
      texture: this.texture,
      srcMatrix: this.matrixFor(doc, offset),
      mask: null,
      opacity: 1,
      blendMode: 'normal',
    });
    this.engine.renderer.copy(surface.target.framebuffer, scratch.texture);
    const after = surface.readPixels(region);

    const ops = [
      ...this.liftOps,
      pixelOp(layerId, { rect: region, data: before }, { rect: region, data: after }),
    ];

    const selectionBefore = this.engine.snapshotSelection(doc);
    if (selectionBefore) {
      const shift = resources.r8(2);
      shift.clear(0, 0, 0, 0);
      this.engine.renderer.copy(shift.framebuffer, resources.selection.texture, {
        srcMatrix: translation(doc, offset),
      });
      resources.selection.restore(shift.texture.readPixels());
      ops.push(selectionOp(selectionBefore, this.engine.snapshotSelection(doc)));
    }
    return ops;
  }

  dispose(): void {
    this.texture.dispose();
  }

  private matrixFor(doc: DocumentMeta, offset: Point): Mat3 {
    return placementMatrix(offsetRect(this.rect, offset), doc.width, doc.height);
  }
}

function translation(doc: DocumentMeta, offset: Point): Mat3 {
  return new Float32Array([
    1,
    0,
    0,
    0,
    1,
    0,
    -offset.x / doc.width,
    -offset.y / doc.height,
    1,
  ]);
}
